// Centralized query keys for TanStack Query
// Following hierarchical structure for efficient cache invalidation

export const queryKeys = {
  // Nudges — the platform notification feed. Contract lives in the knowledge
  // base at zarity/projects/nudges/contract.md
  notifications: {
    all: ["notifications"] as const,
    list: (params?: any) =>
      [...queryKeys.notifications.all, "list", params] as const,
    unreadCount: () => [...queryKeys.notifications.all, "unreadCount"] as const,
    categories: () => [...queryKeys.notifications.all, "categories"] as const,
  },

  // Ping-related query keys
  ping: {
    all: ["ping"] as const,
    chats: {
      all: () => [...queryKeys.ping.all, "chats"] as const,
      list: (params?: any) =>
        [...queryKeys.ping.chats.all(), "list", params] as const,
      detail: (id: string) =>
        [...queryKeys.ping.chats.all(), "detail", id] as const,
      reassignmentTargets: (id: string) =>
        [...queryKeys.ping.chats.detail(id), "reassignment-targets"] as const,
    },
    messages: {
      all: () => [...queryKeys.ping.all, "messages"] as const,
      list: (chatId: string, params?: any) =>
        [...queryKeys.ping.messages.all(), "list", chatId, params] as const,
      infinite: (chatId: string) =>
        [...queryKeys.ping.messages.all(), "infinite", chatId] as const,
      infiniteWithParams: (chatId: string, pageSize: number) =>
        [...queryKeys.ping.messages.infinite(chatId), pageSize] as const,
    },
    internalChats: {
      all: () => [...queryKeys.ping.all, "internalChats"] as const,
      list: (params?: any) =>
        [...queryKeys.ping.internalChats.all(), "list", params] as const,
      detail: (id: string) =>
        [...queryKeys.ping.internalChats.all(), "detail", id] as const,
    },
    internalMessages: {
      all: () => [...queryKeys.ping.all, "internalMessages"] as const,
      list: (chatId: string, params?: any) =>
        [
          ...queryKeys.ping.internalMessages.all(),
          "list",
          chatId,
          params,
        ] as const,
      infinite: (chatId: string) =>
        [...queryKeys.ping.internalMessages.all(), "infinite", chatId] as const,
      infiniteWithParams: (chatId: string, pageSize: number) =>
        [
          ...queryKeys.ping.internalMessages.infinite(chatId),
          pageSize,
        ] as const,
    },
    internalChatUsers: {
      all: () => [...queryKeys.ping.all, "internalChatUsers"] as const,
      list: (params?: any) =>
        [...queryKeys.ping.internalChatUsers.all(), "list", params] as const,
    },
  },

  // User-related query keys
  user: {
    all: ["user"] as const,
    profile: () => [...queryKeys.user.all, "profile"] as const,
  },

  // Conversations query keys (existing functionality)
  conversations: {
    all: ["conversations"] as const,
    list: () => [...queryKeys.conversations.all, "list"] as const,
    detail: (id: string) =>
      [...queryKeys.conversations.all, "detail", id] as const,
  },

  // Nutrition-related query keys
  nutrition: {
    all: ["nutrition"] as const,
    foodItems: {
      all: () => [...queryKeys.nutrition.all, "foodItems"] as const,
      list: (params?: {
        search?: string;
        page?: number;
        page_size?: number;
        appointment_id?: string;
        food_type?: string;
        source?: string;
        has_unpublished_changes?: boolean;
      }) => [...queryKeys.nutrition.foodItems.all(), "list", params] as const,
      detail: (id: number) =>
        [...queryKeys.nutrition.foodItems.all(), "detail", id] as const,
      // Version history is its own key rather than part of the detail: it is a
      // separate console-only endpoint, and it stays valid for a retired food
      // whose detail read may not.
      versions: (id: number) =>
        [...queryKeys.nutrition.foodItems.all(), "versions", id] as const,
      version: (id: number, versionNumber: number) =>
        [
          ...queryKeys.nutrition.foodItems.all(),
          "versions",
          id,
          versionNumber,
        ] as const,
    },
    foodServings: {
      all: () => [...queryKeys.nutrition.all, "foodServings"] as const,
      list: (params?: {
        search?: string;
        is_default?: boolean;
        serving_unit_name?: string;
        food_item_source?: string;
        food_item?: number;
        include_inactive?: boolean;
        ordering?: string;
        page?: number;
        page_size?: number;
      }) =>
        [...queryKeys.nutrition.foodServings.all(), "list", params] as const,
      detail: (id: number) =>
        [...queryKeys.nutrition.foodServings.all(), "detail", id] as const,
    },
    servingUnits: {
      all: () => [...queryKeys.nutrition.all, "servingUnits"] as const,
      list: (params?: { search?: string; ordering?: string }) =>
        [...queryKeys.nutrition.servingUnits.all(), "list", params] as const,
      detail: (code: number) =>
        [...queryKeys.nutrition.servingUnits.all(), "detail", code] as const,
    },
    recipes: {
      all: () => [...queryKeys.nutrition.all, "recipes"] as const,
      list: (params?: {
        search?: string;
        status?: string;
        category?: string;
        ordering?: string;
        page?: number;
        page_size?: number;
      }) => [...queryKeys.nutrition.recipes.all(), "list", params] as const,
      detail: (id: string) =>
        [...queryKeys.nutrition.recipes.all(), "detail", id] as const,
    },
    tagMetadata: {
      all: () => [...queryKeys.nutrition.all, "tagMetadata"] as const,
      get: () => [...queryKeys.nutrition.tagMetadata.all(), "get"] as const,
    },
  },

  // Clinics-related query keys
  clinics: {
    all: ["clinics"] as const,
  },

  // Staff-related query keys
  staff: {
    all: ["staff"] as const,
    roles: () => [...queryKeys.staff.all, "roles"] as const,
    list: (params?: { page?: number; role?: string; search?: string }) =>
      [...queryKeys.staff.all, "list", params] as const,
    detail: (id: string) => [...queryKeys.staff.all, "detail", id] as const,
  },

  diseases: {
    all: ["diseases"] as const,
    list: (params?: {
      search?: string;
      page?: number;
      page_size?: number;
      widely_prevalent?: boolean;
      family_linked?: boolean;
      is_featured?: boolean;
    }) => [...queryKeys.diseases.all, "list", params] as const,
    detail: (id: string) => [...queryKeys.diseases.all, "detail", id] as const,
  },

  consultationRecordings: {
    all: ["consultationRecordings"] as const,
    list: (params?: {
      page?: number;
      page_size?: number;
      search?: string;
      status?: string;
      provider_id?: string;
      date_from?: string;
      date_to?: string;
      sort_by?: string;
      sort_order?: string;
    }) => [...queryKeys.consultationRecordings.all, "list", params] as const,
    segments: (recordingId?: string) =>
      [
        ...queryKeys.consultationRecordings.all,
        "segments",
        recordingId,
      ] as const,
    playback: (recordingId?: string) =>
      [
        ...queryKeys.consultationRecordings.all,
        "playback",
        recordingId,
      ] as const,
    transcript: (recordingId?: string) =>
      [
        ...queryKeys.consultationRecordings.all,
        "transcript",
        recordingId,
      ] as const,
    detail: (recordingId?: string) =>
      [...queryKeys.consultationRecordings.all, "detail", recordingId] as const,
  },

  // Communications-related query keys
  communications: {
    all: ["communications"] as const,
    list: (params?: {
      user_id?: string;
      medium?: string;
      provider?: string;
      status?: string;
      start_date?: string;
      end_date?: string;
      page?: number;
      page_size?: number;
    }) => [...queryKeys.communications.all, "list", params] as const,
    stats: (userId?: string) =>
      [...queryKeys.communications.all, "stats", userId] as const,
  },

  // Staff Communications-related query keys
  staffCommunications: {
    all: ["staffCommunications"] as const,
    list: (params?: {
      console_user_id?: string;
      medium?: string;
      provider?: string;
      status?: string;
      start_date?: string;
      end_date?: string;
      page?: number;
      page_size?: number;
    }) => [...queryKeys.staffCommunications.all, "list", params] as const,
    stats: (consoleUserId?: string) =>
      [...queryKeys.staffCommunications.all, "stats", consoleUserId] as const,
  },

  transactions: {
    all: ["transactions"] as const,
    list: (params?: {
      search?: string;
      status?: string;
      payment_method?: string;
      date_from?: string;
      date_to?: string;
      page?: number;
      page_size?: number;
    }) => [...queryKeys.transactions.all, "list", params] as const,
    detail: (id: string) =>
      [...queryKeys.transactions.all, "detail", id] as const,
  },

  // Appointments-related query keys
  appointments: {
    all: ["appointments"] as const,
    staff: {
      all: (userId?: string) =>
        [...queryKeys.appointments.all, "staff", userId] as const,
      upcoming: (userId?: string, page?: number) =>
        [
          ...queryKeys.appointments.staff.all(userId),
          "upcoming",
          page,
        ] as const,
      ongoing: (userId?: string, page?: number) =>
        [...queryKeys.appointments.staff.all(userId), "ongoing", page] as const,
      history: (userId?: string, page?: number) =>
        [...queryKeys.appointments.staff.all(userId), "history", page] as const,
    },
    adminList: (params?: Record<string, unknown>) =>
      [...queryKeys.appointments.all, "adminList", params] as const,
    collection: (appointmentId: string) =>
      [...queryKeys.appointments.all, "collection", appointmentId] as const,
    awaitingPayments: (page: number, pageSize: number) =>
      [
        ...queryKeys.appointments.all,
        "awaitingPayments",
        page,
        pageSize,
      ] as const,
  },

  // Services / Manage Services query keys
  services: {
    all: ["services"] as const,
    categories: {
      all: () => [...queryKeys.services.all, "categories"] as const,
      list: () => [...queryKeys.services.categories.all(), "list"] as const,
    },
    byCategory: {
      all: () => [...queryKeys.services.all, "byCategory"] as const,
      list: (categoryId: string) =>
        [...queryKeys.services.byCategory.all(), categoryId] as const,
    },
    detail: (serviceId: string) =>
      [...queryKeys.services.all, "detail", serviceId] as const,
    staff: {
      all: () => [...queryKeys.services.all, "staff"] as const,
      list: () => [...queryKeys.services.staff.all(), "list"] as const,
    },
    modes: (serviceId: string) =>
      [...queryKeys.services.all, "modes", serviceId] as const,
  },

  promoCodes: {
    all: ["promoCodes"] as const,
    list: (params?: {
      search?: string;
      status?: string;
      page?: number;
      page_size?: number;
    }) => [...queryKeys.promoCodes.all, "list", params] as const,
    detail: (promoCodeId: number) =>
      [...queryKeys.promoCodes.all, "detail", promoCodeId] as const,
  },

  maintenanceWindows: {
    all: ["maintenanceWindows"] as const,
    list: (params?: Record<string, unknown>) =>
      [...queryKeys.maintenanceWindows.all, "list", params] as const,
    detail: (windowId: number) =>
      [...queryKeys.maintenanceWindows.all, "detail", windowId] as const,
  },

  // Consultation-related query keys
  consultation: {
    all: ["consultation"] as const,

    // Catalog queries
    catalogs: {
      all: () => [...queryKeys.consultation.all, "catalogs"] as const,
      diseases: (params?: Record<string, unknown>) =>
        [...queryKeys.consultation.catalogs.all(), "diseases", params] as const,
      lifestyleTypes: (params?: Record<string, unknown>) =>
        [
          ...queryKeys.consultation.catalogs.all(),
          "lifestyleTypes",
          params,
        ] as const,
      lifestyleFrequencies: () =>
        [
          ...queryKeys.consultation.catalogs.all(),
          "lifestyleFrequencies",
        ] as const,
      activityTypes: (params?: Record<string, unknown>) =>
        [
          ...queryKeys.consultation.catalogs.all(),
          "activityTypes",
          params,
        ] as const,
      activityDurations: () =>
        [
          ...queryKeys.consultation.catalogs.all(),
          "activityDurations",
        ] as const,
      activityFrequencies: () =>
        [
          ...queryKeys.consultation.catalogs.all(),
          "activityFrequencies",
        ] as const,
      surgeries: () =>
        [...queryKeys.consultation.catalogs.all(), "surgeries"] as const,
      investigations: (params?: Record<string, unknown>) =>
        [
          ...queryKeys.consultation.catalogs.all(),
          "investigations",
          params,
        ] as const,
      medicines: (search: string, doseForm: string) =>
        [
          ...queryKeys.consultation.catalogs.all(),
          "medicines",
          search,
          doseForm,
        ] as const,
      medicineAPIs: (search: string) =>
        [
          ...queryKeys.consultation.catalogs.all(),
          "medicineAPIs",
          search,
        ] as const,
      medicineUnits: () =>
        [...queryKeys.consultation.catalogs.all(), "medicineUnits"] as const,
      medicineDurations: () =>
        [
          ...queryKeys.consultation.catalogs.all(),
          "medicineDurations",
        ] as const,
      medicineFrequencies: () =>
        [
          ...queryKeys.consultation.catalogs.all(),
          "medicineFrequencies",
        ] as const,
      doseForms: () =>
        [...queryKeys.consultation.catalogs.all(), "doseForms"] as const,
      investigationsSearch: (search: string, type: string) =>
        [
          ...queryKeys.consultation.catalogs.all(),
          "investigationsSearch",
          search,
          type,
        ] as const,
      trackers: (params?: any) =>
        [...queryKeys.consultation.catalogs.all(), "trackers", params] as const,
      monitoringFrequencies: () =>
        [
          ...queryKeys.consultation.catalogs.all(),
          "monitoringFrequencies",
        ] as const,
    },

    // Patient data queries
    patientData: {
      all: () => [...queryKeys.consultation.all, "patientData"] as const,
      diseaseHistory: (appointmentId: string, source?: string) =>
        source
          ? ([
              ...queryKeys.consultation.patientData.all(),
              "diseaseHistory",
              appointmentId,
              source,
            ] as const)
          : ([
              ...queryKeys.consultation.patientData.all(),
              "diseaseHistory",
              appointmentId,
            ] as const),
      diseaseSummary: (patientId: string) =>
        [
          ...queryKeys.consultation.patientData.all(),
          "diseaseSummary",
          patientId,
        ] as const,
      familyDiseaseHistory: (patientId: string) =>
        [
          ...queryKeys.consultation.patientData.all(),
          "familyDiseaseHistory",
          patientId,
        ] as const,
      lifestyle: (appointmentId: string) =>
        [
          ...queryKeys.consultation.patientData.all(),
          "lifestyle",
          appointmentId,
        ] as const,
      activity: (appointmentId: string) =>
        [
          ...queryKeys.consultation.patientData.all(),
          "activity",
          appointmentId,
        ] as const,
      surgeries: (appointmentId: string) =>
        [
          ...queryKeys.consultation.patientData.all(),
          "surgeries",
          appointmentId,
        ] as const,
      appointmentHistory: (patientId: string, status?: string) =>
        [
          ...queryKeys.consultation.patientData.all(),
          "appointmentHistory",
          patientId,
          status,
        ] as const,
      investigationNotes: (
        appointmentId: string,
        consolidated: boolean = false
      ) =>
        [
          ...queryKeys.consultation.patientData.all(),
          "investigationNotes",
          appointmentId,
          consolidated,
        ] as const,
      prescriptions: (appointmentId: string, consolidated: boolean = false) =>
        [
          ...queryKeys.consultation.patientData.all(),
          "prescriptions",
          appointmentId,
          consolidated,
        ] as const,
      investigationAdvice: (
        appointmentId: string,
        consolidated: boolean = false
      ) =>
        [
          ...queryKeys.consultation.patientData.all(),
          "investigationAdvice",
          appointmentId,
          consolidated,
        ] as const,
      monitoringPlan: (patientId: string, appointmentId?: string) =>
        [
          ...queryKeys.consultation.patientData.all(),
          "monitoringPlan",
          patientId,
          appointmentId ?? "now",
        ] as const,
      monitoringPlanAll: (patientId: string) =>
        [
          ...queryKeys.consultation.patientData.all(),
          "monitoringPlan",
          patientId,
        ] as const,
      monitoringPlanChanges: (patientId: string) =>
        [
          ...queryKeys.consultation.patientData.all(),
          "monitoringPlanChanges",
          patientId,
        ] as const,
      generalAdvice: (
        appointmentId: string,
        consolidated: boolean = false,
        parentType?: string
      ) =>
        [
          ...queryKeys.consultation.patientData.all(),
          "generalAdvice",
          appointmentId,
          consolidated,
          parentType,
        ] as const,
      treatmentOverview: (id: string, filters?: Record<string, unknown>) =>
        [
          ...queryKeys.consultation.patientData.all(),
          "treatmentOverview",
          id,
          filters,
        ] as const,
      patientSearch: (query: string) =>
        [
          ...queryKeys.consultation.patientData.all(),
          "patientSearch",
          query,
        ] as const,
    },
  },
} as const;

// Helper functions for cache invalidation
export const invalidateQueries = {
  // Ping invalidations
  pingChats: () => ({ queryKey: queryKeys.ping.chats.all() }),
  pingChatDetail: (id: string) => ({
    queryKey: queryKeys.ping.chats.detail(id),
  }),
  pingMessages: (chatId: string) => ({
    queryKey: queryKeys.ping.messages.list(chatId),
  }),
  allPingData: () => ({ queryKey: queryKeys.ping.all }),

  // Internal chat invalidations
  internalChats: () => ({ queryKey: queryKeys.ping.internalChats.all() }),
  internalChatDetail: (id: string) => ({
    queryKey: queryKeys.ping.internalChats.detail(id),
  }),
  internalMessages: (chatId: string) => ({
    queryKey: queryKeys.ping.internalMessages.list(chatId),
  }),
  internalChatUsers: () => ({
    queryKey: queryKeys.ping.internalChatUsers.all(),
  }),

  // User invalidations
  userProfile: () => ({ queryKey: queryKeys.user.profile() }),

  // Conversation invalidations
  conversations: () => ({ queryKey: queryKeys.conversations.all }),

  // Nutrition invalidations
  nutritionFoodItems: () => ({ queryKey: queryKeys.nutrition.foodItems.all() }),
  nutritionFoodItemList: (params?: {
    search?: string;
    page?: number;
    page_size?: number;
    appointment_id?: string;
    food_type?: string;
  }) => ({ queryKey: queryKeys.nutrition.foodItems.list(params) }),
  nutritionFoodItemDetail: (id: number) => ({
    queryKey: queryKeys.nutrition.foodItems.detail(id),
  }),
  nutritionFoodServings: () => ({
    queryKey: queryKeys.nutrition.foodServings.all(),
  }),
  nutritionFoodServingList: (params?: {
    search?: string;
    is_default?: boolean;
    serving_unit_name?: string;
    food_item_source?: string;
    food_item?: number;
    include_inactive?: boolean;
    ordering?: string;
    page?: number;
    page_size?: number;
  }) => ({ queryKey: queryKeys.nutrition.foodServings.list(params) }),
  nutritionFoodServingDetail: (id: number) => ({
    queryKey: queryKeys.nutrition.foodServings.detail(id),
  }),
  nutritionServingUnits: () => ({
    queryKey: queryKeys.nutrition.servingUnits.all(),
  }),
  nutritionServingUnitList: (params?: {
    search?: string;
    ordering?: string;
  }) => ({ queryKey: queryKeys.nutrition.servingUnits.list(params) }),
  nutritionServingUnitDetail: (code: number) => ({
    queryKey: queryKeys.nutrition.servingUnits.detail(code),
  }),
  nutritionTagMetadata: () => ({
    queryKey: queryKeys.nutrition.tagMetadata.all(),
  }),

  // Staff invalidations
  staffList: () => ({ queryKey: queryKeys.staff.all }),
  staffDetail: (id: string) => ({ queryKey: queryKeys.staff.detail(id) }),

  diseaseLibrary: () => ({ queryKey: queryKeys.diseases.all }),
  diseaseLibraryDetail: (id: string) => ({
    queryKey: queryKeys.diseases.detail(id),
  }),

  // Communications invalidations
  communicationsList: () => ({ queryKey: queryKeys.communications.all }),
  communicationsStats: (userId?: string) => ({
    queryKey: queryKeys.communications.stats(userId),
  }),

  // Staff Communications invalidations
  staffCommunicationsList: () => ({
    queryKey: queryKeys.staffCommunications.all,
  }),
  staffCommunicationsStats: (consoleUserId?: string) => ({
    queryKey: queryKeys.staffCommunications.stats(consoleUserId),
  }),

  // Appointments invalidations
  staffAppointments: (userId?: string) => ({
    queryKey: queryKeys.appointments.staff.all(userId),
  }),
  staffUpcomingAppointments: (userId?: string, page?: number) => ({
    queryKey: queryKeys.appointments.staff.upcoming(userId, page),
  }),
  staffOngoingAppointments: (userId?: string, page?: number) => ({
    queryKey: queryKeys.appointments.staff.ongoing(userId, page),
  }),
  staffHistoryAppointments: (userId?: string, page?: number) => ({
    queryKey: queryKeys.appointments.staff.history(userId, page),
  }),

  // Consultation invalidations
  consultationCatalogs: () => ({
    queryKey: queryKeys.consultation.catalogs.all(),
  }),
  diseases: (params?: Record<string, unknown>) => ({
    queryKey: queryKeys.consultation.catalogs.diseases(params),
  }),
  lifestyleTypes: (params?: Record<string, unknown>) => ({
    queryKey: queryKeys.consultation.catalogs.lifestyleTypes(params),
  }),
  lifestyleFrequencies: () => ({
    queryKey: queryKeys.consultation.catalogs.lifestyleFrequencies(),
  }),
  activityTypes: (params?: Record<string, unknown>) => ({
    queryKey: queryKeys.consultation.catalogs.activityTypes(params),
  }),
  activityDurations: () => ({
    queryKey: queryKeys.consultation.catalogs.activityDurations(),
  }),
  activityFrequencies: () => ({
    queryKey: queryKeys.consultation.catalogs.activityFrequencies(),
  }),
  surgeries: () => ({ queryKey: queryKeys.consultation.catalogs.surgeries() }),
  investigations: (params?: Record<string, unknown>) => ({
    queryKey: queryKeys.consultation.catalogs.investigations(params),
  }),
  medicines: () => ({
    queryKey: [...queryKeys.consultation.catalogs.all(), "medicines"],
  }),
  medicineAPIs: () => ({
    queryKey: [...queryKeys.consultation.catalogs.all(), "medicineAPIs"],
  }),

  patientDiseaseHistory: (appointmentId: string, source?: string) => ({
    queryKey: queryKeys.consultation.patientData.diseaseHistory(
      appointmentId,
      source
    ),
  }),
  diseaseSummary: (patientId: string) => ({
    queryKey: queryKeys.consultation.patientData.diseaseSummary(patientId),
  }),
  patientFamilyDiseaseHistory: (patientId: string) => ({
    queryKey:
      queryKeys.consultation.patientData.familyDiseaseHistory(patientId),
  }),
  patientLifestyle: (appointmentId: string) => ({
    queryKey: queryKeys.consultation.patientData.lifestyle(appointmentId),
  }),
  patientActivity: (appointmentId: string) => ({
    queryKey: queryKeys.consultation.patientData.activity(appointmentId),
  }),
  patientSurgeries: (appointmentId: string) => ({
    queryKey: queryKeys.consultation.patientData.surgeries(appointmentId),
  }),
  appointmentHistory: (patientId: string, status?: string) => ({
    queryKey: queryKeys.consultation.patientData.appointmentHistory(
      patientId,
      status
    ),
  }),
  patientInvestigationNotes: (
    appointmentId: string,
    consolidated: boolean = false
  ) => ({
    queryKey: queryKeys.consultation.patientData.investigationNotes(
      appointmentId,
      consolidated
    ),
  }),
  patientPrescriptions: (
    appointmentId: string,
    consolidated: boolean = false
  ) => ({
    queryKey: queryKeys.consultation.patientData.prescriptions(
      appointmentId,
      consolidated
    ),
  }),
  patientInvestigationAdvice: (
    appointmentId: string,
    consolidated: boolean = false
  ) => ({
    queryKey: queryKeys.consultation.patientData.investigationAdvice(
      appointmentId,
      consolidated
    ),
  }),
  patientGeneralAdvice: (
    appointmentId: string,
    consolidated: boolean = false,
    parentType?: string
  ) => ({
    queryKey: queryKeys.consultation.patientData.generalAdvice(
      appointmentId,
      consolidated,
      parentType
    ),
  }),
  trackers: (params?: any) => ({
    queryKey: queryKeys.consultation.catalogs.trackers(params),
  }),
};
